// ServiceOverview/CmdEnvsEngine.js
// FE Engine Pattern V2 (rk=beeca47db94541d594bc4aa830f62672) — vendor command envs per instance,
// initial REST fetch + SignalR OnCmdEnvsUpdated. Changes are posted, the result comes back via push. Singleton.
import { events } from '@/services/wss/SystemEvents';
import ComponentNotifier from '../../../services/wss/ComponentNotifier';
import WssEventHandlerBase from '@/services/engines/WssEventHandlerBase';
import cmdEnvsCache from './CmdEnvsCache';
import cmdEnvsFetcher from './CmdEnvsFetcher';

const _name = 'CmdEnvsEngine';

class CmdEnvsEngine extends WssEventHandlerBase {
    _name = _name;

    // no this.clear() here — boot-fetch may already have filled the cache
    bootup() {
        super.bootup();
        this._unsubscribe();

        this._unsub = this._wsNotifier.subscribe(events.OnCmdEnvsUpdated, this.onMessage);
    }

    clear() {
        super.clear();
        this._unsubscribe();
        cmdEnvsCache.clear();
    }

    /**
     * BE push — array of CmdEnvs, overwrites per vendor instance.
     * @param {object[]} data
     */
    onMessage = (data) => {
        try {
            cmdEnvsCache.addOrUpdate(data);
            this._notify();
        } catch (error) {
            this.logError('onMessage', error.message);
        }
    };

    /**
     * Initial fetch — last known cmd envs of all vendor instances.
     * @returns {Promise<void>}
     */
    fetch = async () => {
        try {
            const items = await cmdEnvsFetcher.fetch();
            if (!items) return;

            cmdEnvsCache.setAll(items);
            this._notify();
        } catch (error) {
            this.logError('fetch', error.message);
        }
    };

    /**
     * @param {string} app
     * @param {string} machine
     * @returns {object|undefined} { app, machine, envs }
     */
    get = (app, machine) => cmdEnvsCache.get(app, machine);

    /**
     * Add or remove one env for a vendor instance, posts the full set.
     * @param {string} app
     * @param {string} machine
     * @param {string} env
     * @returns {Promise<boolean>} request accepted
     */
    toggleEnv = async (app, machine, env) => {
        try {
            const item = cmdEnvsCache.get(app, machine);
            if (!item) return false;

            const envs = this._toggle(item.envs, env);
            return await cmdEnvsFetcher.post([{ app, machine, envs }]);
        } catch (error) {
            this.logError('toggleEnv', error.message);
            return false;
        }
    };

    _toggle = (envs, env) => {
        if (envs.includes(env)) return envs.filter((e) => e !== env);

        return [...envs, env];
    };

    _unsubscribe = () => {
        if (this._unsub) this._unsub();
        this._unsub = null;
    };

    _notify = () => {
        ComponentNotifier.notify(_name, 'dataChanged', {});
    };
}

const cmdEnvsEngine = new CmdEnvsEngine();
export default cmdEnvsEngine;
